import { FC, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

import useDropdown, { useDropdownProps } from '#/hooks/useDropdown';
import { remify } from '#/service/utils';

import { ArrowIcon, IconContainer } from '../Accordion/AccordionItem.styled';
import Button from '../Button/Button';
import { fadeInAnimation, fadeOutAnimation } from '../common';

export interface Props extends useDropdownProps {
  minWidth?: string;
  maxHeight?: string;
  /** Adds functionality for filtering options by the user */
  searchable?: boolean;
  /** Placeholder text for filtering input box */
  placeholder?: string;
  /** Name that will be displayed on the select when nothing is selected yet */
  title?: string;
  /** id is required in order to meet accessibility standards */
  id: string;
  borderRadius?: number;
}

// TODO: Can be refactored to be more generic in use
export const MvpdSelect: FC<Props> = ({
  borderRadius,
  onSelect,
  options,
  selectedOption,
  maxHeight,
  minWidth,
  searchable,
  placeholder,
  title,
  id,
  placement,
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const { isOpen, setIsOpen, triggerButtonRef, handleKeyDown, highlighted } =
    useDropdown({
      onSelect,
      options: filteredOptions,
      selectedOption,
      placement,
    });
  const searchRef = useRef<HTMLInputElement>(null);

  const [filter, setFilter] = useState<string>('');

  useEffect(() => {
    const lowerFilter = filter.toLowerCase();
    setFilteredOptions(
      lowerFilter
        ? options.filter((el) => el.value.toLowerCase().includes(lowerFilter))
        : options
    );
  }, [filter, options]);

  function handleDropdownClick() {
    setIsOpen(!isOpen);

    if (!isOpen && searchable) {
      setTimeout(() => {
        searchRef.current?.focus();
      });
    }
  }

  const handleOptionCick = (value) => () => {
    onSelect(value);
    setIsOpen(false);
  };

  const handleBlur = (ev) => {
    if (!ev.currentTarget.contains(ev.relatedTarget)) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <DropdownTrigger
        $borderRadius={borderRadius}
        $minWidth={minWidth}
        aria-activedescendant={highlighted || selectedOption?.key}
        aria-controls="listbox1"
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label`}
        id={`${id}-combo`}
        onBlur={handleBlur}
        onClick={handleDropdownClick}
        onKeyDown={handleKeyDown}
        ref={triggerButtonRef}
        role="combobox"
      >
        {isOpen && searchable ? (
          <StyledSearchBox
            id="filter"
            onChange={(el) => setFilter(el.target.value)}
            onClick={(ev) => ev.stopPropagation()}
            placeholder={placeholder}
            ref={searchRef}
            type="search"
            value={filter}
          />
        ) : (
          <>
            {selectedOption && (
              <Value id={`${id}-label`}>{selectedOption?.value}</Value>
            )}
            {!selectedOption && title && <Value>{title}</Value>}
          </>
        )}

        <DropdownIconContainer $open={isOpen}>
          <DropdownIcon name="chevron_down" />
        </DropdownIconContainer>
      </DropdownTrigger>
      <CSSTransition in={isOpen} timeout={500} unmountOnExit={false}>
        <DropdownContainer
          $borderRadius={borderRadius}
          $maxHeight={maxHeight}
          $minWidth={minWidth}
          $open={isOpen}
          aria-labelledby={`${id}-label`}
          id={`${id}-listbox`}
          role="listbox"
        >
          <DropdownItemsWrapper>
            {filteredOptions.map((option) => {
              const selected = option.key === highlighted;

              return (
                <DropdownItem
                  aria-selected={selected}
                  id={option.key}
                  key={option.key}
                  onClick={handleOptionCick(option.key)}
                  role="option"
                  tabIndex={-1}
                >
                  {option.value}
                </DropdownItem>
              );
            })}
          </DropdownItemsWrapper>
        </DropdownContainer>
      </CSSTransition>
    </>
  );
};

export const DropdownIcon = styled(ArrowIcon)`
  height: ${remify(16)};
  width: ${remify(16)};
`;

export const DropdownTrigger = styled(Button)<{
  $borderRadius?: number;
  $minWidth?: string;
}>`
  display: flex;
  justify-content: center;
  ${({ $minWidth }) => !!$minWidth && `min-width: ${$minWidth};`}
  color: var(--pageHeadingForeground);
  background-color: var(--pageSecondaryBackground);
  border-radius: ${({ $borderRadius }) => remify($borderRadius || 4)};
  width: 100%;
  padding: ${remify(12)} ${remify(16)};
  justify-content: space-between;
`;

export const DropdownContainer = styled.div<{
  $open?: boolean;
  $borderRadius?: number;
  $minWidth?: string;
  $maxHeight?: string;
}>`
  width: 100%;
  ${({ $minWidth }) => !!$minWidth && `min-width: ${$minWidth};`}
  color: var(--pageHeadingForeground);
  background-color: var(--pageSecondaryBackground);
  border-radius: ${({ $borderRadius }) => remify($borderRadius || 4)};
  flex-direction: column;
  padding: 0;
  animation-fill-mode: both;
  display: none;
  ${({ $maxHeight }) =>
    !!$maxHeight &&
    `
    max-height: ${$maxHeight};
    overflow-y: auto;
  `}

  &.enter {
    display: flex;
    animation-name: ${fadeInAnimation};
    animation-duration: 200ms;
    animation-timing-function: ease;
  }

  &.exit {
    display: flex;
    animation-name: ${fadeOutAnimation};
    animation-duration: 200ms;
    animation-timing-function: ease;
  }

  &.enter-done {
    display: flex;
  }

  &.exit-done {
    display: none;
  }
`;

export const DropdownItem = styled(Button)`
  text-align: left;
  height: 40px;
  padding: ${remify(12)} ${remify(24)};
  background-color: var(--pageSecondaryBackground);
  color: var(--pageParagraphForegroundSubtle);
  border-radius: 0;
  border: none;

  &[aria-selected='true'] {
    color: var(--pageParagraphForeground);
  }

  &:hover {
    opacity: 1;
  }
`;

export const Value = styled.span`
  padding: 0 ${remify(8)};
`;

export const DropdownItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DropdownIconContainer = styled(IconContainer)`
  padding: 0 ${remify(16)};
  ${({ $open }) => $open && `transform: rotateX(180deg);`}
`;

const StyledSearchBox = styled.input`
  font: var(--fontStyleSubtitle);
  appearance: none;
  background: transparent;
  border: none;
  margin: 0;
  padding: 0;
  color: inherit;
  width: 100%;
  line-height: 1;

  &.focus-visible {
    outline: none;
  }
`;
